import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { format } from 'date-fns';
import { Control, useWatch } from 'react-hook-form';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import {
  CircuitStepDaytime,
  compareCircuitStepDayTime,
} from '@/types/circuits/CircuitStepDaytime';
import { OptionalBenefitComputationFn } from '../EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import EstimateDayStepCard from './EstimateDayStepCard';
import { getBenefitTypeName } from '@/utils/benefits/getBenefitTypeName';
import { BenefitType } from '@/types/benefits/benefitTypes';
import EstimateDayCarRentalStepCard from './EstimateDayCarRentalStepCard';
import EstimateDayHotelStepCard from './EstimateDayHotelStepCard';
import EstimateDayStepSummaryCard from './EstimateDayStepSummaryCard';
import clsx from 'clsx';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';
import { useTranslation } from 'react-i18next';

interface EstimateDayCardProps {
  control: Control<EstimateFormData>;
  dayIndex: number;
  date: Date;
  circuitDay: CircuitDayFormData;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: Dispatch<
    SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  adultParticipants: EstimateFormData['adults'];
  childParticipants: EstimateFormData['children'];
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  isViewMode?: boolean;
  language?: SupportedLocale;
  showTranslation?: boolean;
  isEditableCircuit?: boolean;
}

const EstimateDayCard = ({
  control,
  dayIndex,
  date,
  circuitDay,
  computeOptionalBenefitPrice,
  selectedOptions,
  setSelectedOptions,
  adultParticipants,
  childParticipants,
  currency = 'EUR',
  creationDate,
  accessToken,
  isViewMode = false,
  language,
  showTranslation = true,
  isEditableCircuit = false,
}: EstimateDayCardProps) => {
  const orderedSteps = useMemo(
    () =>
      circuitDay.steps
        .flatMap((step) => [
          step.transport,
          step.accommodations,
          step.activities,
          step.meals,
        ])
        .filter(
          ({ standardBenefit, optionalBenefits, nbDays }) =>
            (standardBenefit || optionalBenefits?.length) &&
            (nbDays && nbDays > 1
              ? standardBenefit
                ? standardBenefit.indexFirstDay === dayIndex
                : optionalBenefits.length > 0
                  ? optionalBenefits[0].indexFirstDay === dayIndex
                  : false
              : true),
        )
        .toSorted((a, b) => {
          if (!a.time && !b.time) return 0;
          if (!b.time) return -1;
          if (!a.time) return 1;
          const timeA = a.time?.value ?? CircuitStepDaytime.Night;
          const timeB = b.time?.value ?? CircuitStepDaytime.Night;
          return compareCircuitStepDayTime(timeA, timeB);
        }),
    [circuitDay.steps, dayIndex],
  );

  const getEstimateStepType = useCallback(
    (step: CircuitDayFormData['steps'][number]['transport']) =>
      step.standardBenefit
        ? getBenefitTypeName(
            step.standardBenefit.type === 'ACTIVITY'
              ? (step.standardBenefit.activity?.activityType ??
                  step.standardBenefit.type)
              : step.standardBenefit.type,
          )
        : step.optionalBenefits.length > 0
          ? getBenefitTypeName(
              step.optionalBenefits[0].type === 'ACTIVITY'
                ? (step.optionalBenefits[0].activity?.activityType ??
                    step.optionalBenefits[0].type)
                : step.optionalBenefits[0].type,
            )
          : null,
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1 className="text-xl font-medium text-black">
            <CircuitDayParagraph
              control={control}
              dayIndex={dayIndex}
              field="stepTitle"
              date={date}
              language={language}
              showTranslation={showTranslation}
              isEditableCircuit={isEditableCircuit}
            />
          </h1>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <CircuitDayParagraph
          control={control}
          dayIndex={dayIndex}
          language={language}
          showTranslation={showTranslation}
          isEditableCircuit={isEditableCircuit}
        />
        {orderedSteps.map((step) => (
          <Fragment key={`step-${dayIndex}-${step.id}`}>
            {isViewMode ? (
              <EstimateDayStepSummaryCard
                step={step}
                date={date}
                dayIndex={dayIndex}
                selectedOptions={selectedOptions}
                accessToken={accessToken}
                computeOptionalBenefitPrice={computeOptionalBenefitPrice}
                currency={currency}
                creationDate={creationDate}
                language={language}
              />
            ) : getEstimateStepType(step) === BenefitType.VEHICLE ? (
              <EstimateDayCarRentalStepCard
                step={step}
                dayIndex={dayIndex}
                date={date}
                computeOptionalBenefitPrice={computeOptionalBenefitPrice}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                nbAdults={adultParticipants.length}
                nbChildren={childParticipants.length}
                currency={currency}
                creationDate={creationDate}
                language={language}
                accessToken={accessToken}
                isViewMode={isViewMode}
              />
            ) : getEstimateStepType(step) === BenefitType.HOTEL ? (
              <EstimateDayHotelStepCard
                step={step}
                dayIndex={dayIndex}
                date={date}
                computeOptionalBenefitPrice={computeOptionalBenefitPrice}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                adultParticipants={adultParticipants}
                childParticipants={childParticipants}
                currency={currency}
                creationDate={creationDate}
                language={language}
                accessToken={accessToken}
                isViewMode={isViewMode}
              />
            ) : (
              <EstimateDayStepCard
                step={step}
                dayIndex={dayIndex}
                date={date}
                computeOptionalBenefitPrice={computeOptionalBenefitPrice}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                nbParticipants={
                  adultParticipants.length + childParticipants.length
                }
                currency={currency}
                creationDate={creationDate}
                language={language}
                accessToken={accessToken}
                isViewMode={isViewMode}
              />
            )}
          </Fragment>
        ))}
      </CardContent>
    </Card>
  );
};

export default EstimateDayCard;

interface CircuitDayParagraphProps {
  control: Control<EstimateFormData>;
  dayIndex: number;
  date?: Date;
  field?: 'stepTitle' | 'description';
  language?: SupportedLocale;
  showTranslation?: boolean;
  isEditableCircuit?: boolean;
}

const CircuitDayParagraph = ({
  control,
  dayIndex,
  date,
  field = 'description',
  language,
  showTranslation = true,
  isEditableCircuit = false,
}: CircuitDayParagraphProps) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const description = useWatch({
    control,
    name: `circuit.circuitDays.${dayIndex}.${field}`,
  });
  const translations = useWatch({
    control,
    name: `circuit.translations`,
  });

  const translatedDescription = (() => {
    if (!language || !showTranslation || isEditableCircuit) return description;
    return (
      translations?.[language]?.circuitDays?.[dayIndex]?.[field] ?? description
    );
  })();

  const appendedText = useMemo(() => {
    if (field === 'description' || !date) return '';
    return `${format(date, 'PPPP', { locale: LOCALES_MAP[currentLanguage] })} - ${t('circuits.day_', { index: dayIndex + 1 })}${translatedDescription ? ' - ' : ''}`;
  }, [currentLanguage, date, dayIndex, field, t, translatedDescription]);

  return (
    <div className="flex justify-between items-start gap-6 w-full">
      <p
        className={clsx({
          'text-xl font-medium text-black': field === 'stepTitle',
          'text-base font-normal text-gray-800 w-full whitespace-pre-wrap':
            field === 'description',
        })}
      >
        {appendedText}
        {translatedDescription}
      </p>
    </div>
  );
};
